import type { FormBuilderHandle } from '@ingenico/react-form-builder';
import { useBusinessServiceRequest } from '@ingenico/react-section-utils';
import type { RefObject } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { tap } from 'rxjs';
import type { Subscription } from 'rxjs';
import { DeviceManagementService } from '../domain/business/services/deviceManagement.service';

interface GetAssetLogoProps {
  readonly childRef: RefObject<FormBuilderHandle>;
  readonly hasInitializedRef: boolean;
  readonly logoId: string | null | undefined;
}

export function useGetAssetLogo({ childRef, hasInitializedRef, logoId }: GetAssetLogoProps) {
  const [hasInternallyInitialized, setHasInternallyInitialized] = useState(false);

  const assetQuery = useBusinessServiceRequest(
    DeviceManagementService,
    useCallback(
      (deviceManagementService) => (logoId ? deviceManagementService.downloadLogoByIdV2(logoId) : null),
      [logoId],
    ),
  );

  useEffect(() => {
    let subscription: Subscription | undefined;
    if (childRef.current) {
      subscription = childRef.current.initialized$
        .pipe(
          tap((value: boolean) => {
            setHasInternallyInitialized(value);
          }),
        )
        .subscribe();
    }
    return () => subscription?.unsubscribe();
  }, [childRef, hasInitializedRef]);

  useEffect(() => {
    if (assetQuery.isSuccess && hasInternallyInitialized) {
      childRef.current?.setLogoAsset(assetQuery.data);
    }
  }, [assetQuery.data, assetQuery.isSuccess, childRef, hasInternallyInitialized]);
}
